/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Vers.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, ComponentProps, Ref } from 'preact';
import { forwardRef, ElementRef } from 'preact/compat';
import { useHover } from '../hooks/UNSAFE_useHover';
import { useActive } from '../hooks/UNSAFE_useActive';
import { useColorScheme } from '../hooks/UNSAFE_useColorScheme';
import { classNames } from '../utils/UNSAFE_classNames';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { BaseButtonVariantOptions } from './themes/BaseButtonStyles.css';
import { BaseButtonRedwoodTheme } from './themes/redwood/BaseButtonTheme';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { BareButton } from '#/UNSAFE_BareButton';
import { TestIdProps } from '../hooks/UNSAFE_useTestId';
import { processGlobals } from '../utils/UNSAFE_buttonUtils';
import type { ButtonIntrinsicProps, ButtonIntrinsicAriaProps } from '../utils/UNSAFE_buttonUtils';

type BaseButtonElementDetails =
  | {
      type: 'button';
      buttonType?: 'button' | 'submit';
      isFocusable?: never;
      isLabel?: never;
      isSwitch?: boolean;
    }
  | {
      type: 'a';
      buttonType?: never;
      href: string;
      target?: string;
      rel?: string;
      isFocusable?: never;
      isLabel?: never;
      isSwitch?: never;
    }
  | {
      type: 'span';
      buttonType?: never;
      isFocusable?: boolean;
      isLabel?: boolean;
      isSwitch?: boolean;
    };

export type StylingOptions =
  | 'default'
  | 'min'
  | 'embedded'
  | 'container'
  | 'unstyled'
  | 'fill'
  | 'noBorderRadiusEnd'
  | 'noBorderRadiusStart'
  | 'noBorderEnd'
  | 'noBorderStart'
  | 'active'
  | 'selected'
  | 'buttonSetItem'
  | 'trailingItem'
  | 'widthEqual'
  | 'hiddenLabel'
  | 'noHoverActive'
  | 'widthAuto'
  | 'newStackingContext'
  | 'noUserSelect';

export type Props = ButtonIntrinsicProps &
  ButtonIntrinsicAriaProps &
  DimensionProps &
  TestIdProps & {
    /**
     *The default slot is the button's text label. The oj-c-base-button element accepts plain text or DOM nodes as children for the default slot."
     */
    children?: ComponentChildren;

    /**
     * Specifies that the button be read-only.
     */
    isReadOnly?: boolean;

    /**
     * Specifies that the button element should be disabled.
     */
    isDisabled?: boolean;

    /**
     * Specifies if repeated events are dispatched for key or pointer held down
     */
    isRepeat?: boolean;

    /**
     * Specifies if Button should use only a click handler, not keyup/down, ptr up/down
     */
    isClick?: boolean;

    /**
     *
     * Indicates in what states the button has chrome (background and border).
     *     "ghost": "Ghost buttons are the least prominent variation.  Ghost buttons are
     * useful for performing low-priority tasks, such as manipulating the UI.",
     *     "borderless"': "Borderless buttons are a more prominent variation. Borderless
     * buttons are useful for supplemental actions that require minimal emphasis.",
     *     "outlined"': "Outlined buttons are salient, but lighter weight than
     * solid buttons. Outlined buttons are useful for secondary actions.",
     *     "solid"': "Solid buttons stand out, and direct the user's attention to the
     *  most important actions in the UI.",
     *     "callToAction"': "A Call To Action (CTA) button guides the user to take or
     * complete the action that is the main goal of the page or page section. There
     * should only be one CTA button on a page at any given time.",
     *     "danger"': "A Danger button alerts the user to a dangerous situation."
     */
    variant?: 'ghost' | 'borderless' | 'outlined' | 'solid' | 'callToAction' | 'danger';

    /**
     * The size indicates how large the button is rendered: 'xs', "sm", "md" (default), "lg".
     */
    size?: 'xs' | 'sm' | 'md' | 'lg';

    /**
     * Edge can be used to specify that the button is intended to go at the bottom of
     * a region, stretching from left to right: "bottom" or "none" (default).
     */
    edge?: 'none' | 'bottom';

    /**
     * Styling can be used for customizing buttons by removing label padding ("min") or
     * removing all styling ("unstyled").
     */
    styling?: StylingOptions[];

    /**
     * Triggered when a button is clicked, whether by keyboard, mouse, or touch events.
     */
    onAction?: ComponentProps<typeof BareButton>['onAction'];

    /**
     * Indicate element type and associated details
     */
    elementDetails?: BaseButtonElementDetails;
  };

type ButtonHandle = ElementRef<typeof BareButton>;

const interpolations = [...Object.values(dimensionInterpolations)];
const styleInterpolations = mergeInterpolations<Props>(interpolations);

const clientHints = getClientHints();
// :active only works on IOS devices if a touch handler exists
const iosProps = clientHints.platform === 'ios' ? { ontouchstart: function () {} } : {};
const isHybrid = getClientHints().hoverSupport === 'events';

export const BaseButton = forwardRef(
  (
    {
      variant,
      isReadOnly = false,
      isDisabled = false,
      isRepeat = false,
      isClick,
      size,
      onAction,
      elementDetails = { type: 'button', buttonType: 'button' },
      styling = ['default'],
      edge = 'none',
      testId,
      ...props
    }: Props,
    ref: Ref<ButtonHandle>
  ) => {
    const isStyled = (name: StylingOptions) => {
      return styling.indexOf(name) > -1;
    };
    const { globalEventProps, ariaProps } = processGlobals(props);
    const { hoverProps, isHover } = useHover({ isDisabled: !isHybrid });
    const { activeProps, isActive } = useActive({ isDisabled: isReadOnly });
    const { class: cls, ...styles } = styleInterpolations({
      ...props,
      ...globalEventProps
    });

    const ElementType = elementDetails.type;
    const isSpan = ElementType == 'span';
    const colorScheme = useColorScheme();

    const { variantClasses } = useComponentTheme<BaseButtonVariantOptions>(BaseButtonRedwoodTheme, {
      size,
      variant,
      unstyled: isStyled('unstyled') ? 'isUnstyled' : 'notUnstyled',
      embedded: isStyled('embedded') ? 'isEmbedded' : 'notEmbedded',
      container: isStyled('container') ? 'isContainer' : 'notContainer',
      noBorderRadiusStart: isStyled('noBorderRadiusStart')
        ? 'isNoBorderRadiusStart'
        : 'notNoBorderRadiusStart',
      noBorderRadiusEnd: isStyled('noBorderRadiusEnd')
        ? 'isNoBorderRadiusEnd'
        : 'notNoBorderRadiusEnd',
      noBorderStart: isStyled('noBorderStart') ? 'isNoBorderStart' : 'notNoBorderStart',
      noBorderEnd: isStyled('noBorderEnd') ? 'isNoBorderEnd' : 'notNoBorderEnd',
      activeStyle: isStyled('active') ? 'isActiveStyle' : 'notActiveStyle',
      selectedStyle: isStyled('selected') ? 'isSelectedStyle' : 'notSelectedStyle',
      buttonSetItem: isStyled('buttonSetItem') ? 'isButtonSetItem' : 'notButtonSetItem',
      trailingItem: isStyled('trailingItem') ? 'isTrailingItem' : 'notTrailingItem',
      fill: isStyled('fill') ? 'isFill' : 'notFill',
      min: isStyled('min') ? 'isMin' : 'notMin',
      disabled: isDisabled ? 'isDisabled' : 'notDisabled',
      edge,
      hybrid: isHybrid ? 'isHybrid' : 'notHybrid',
      active: isActive ? 'isActive' : 'notActive',
      hover: isHover ? 'isHover' : 'notHover',
      span: isSpan ? 'isSpan' : 'notSpan',
      widthEqual: isStyled('widthEqual') ? 'isWidthEqual' : 'notWidthEqual',
      widthAuto: isStyled('widthAuto') ? 'isWidthAuto' : 'notWidthAuto',
      hiddenLabel: isStyled('hiddenLabel') ? 'isHidden' : 'notHidden',
      noHoverActive: isStyled('noHoverActive') ? 'isNoHoverActive' : 'notNoHoverActive',
      newStackingContext: isStyled('newStackingContext')
        ? 'isNewStackingContext'
        : 'notNewStackingContext',
      noUserSelect: isStyled('noUserSelect') ? 'notUserSelect' : 'isUserSelect',
      colorScheme
    });

    const legacyGlobals = {
      autofocus: props.autofocus,
      title: props.title
    };

    const mergedProps = mergeProps(
      iosProps,
      ariaProps,
      hoverProps,
      isStyled('container') ? {} : activeProps,
      globalEventProps,
      legacyGlobals
    );

    const wrapperClasses = classNames([cls, variantClasses]);

    return (
      <BareButton
        ref={ref}
        isDisabled={isDisabled || isReadOnly}
        isClick={isClick}
        classNames={wrapperClasses}
        style={styles}
        testId={testId}
        onAction={onAction}
        autofocus={props['autofocus']}
        elementDetails={elementDetails}
        isRepeat={isRepeat}
        {...ariaProps}
        {...mergedProps}>
        {props.children}
      </BareButton>
    );
  }
);

BaseButton.displayName = 'BaseButton';
